import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { toast } from "react-toastify";
import api from "../../api";
import VideoExplanationPlayer from "../../components/VideoExplanationPlayer";
import VideoVerifyDialog from "./VideoVerifyDialog";
import { StateChip, TypeChip, VerificationChip, canVerifyVideos } from "./shared";

const COVERAGE = [
  { value: "awaiting_verification", label: "Awaiting verification" },
  { value: "rejected", label: "Has rejected videos" },
  { value: "verified_unpublished", label: "Verified, not published" },
  { value: "fully_verified", label: "Fully verified" },
  { value: "finished", label: "All with videos" },
];

/** One set's finished videos, each with its verdict and a Verify button. */
function SetVideos({ set, canVerify, onChanged }) {
  const [rows, setRows] = useState(null);
  const [verifying, setVerifying] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/explanation-videos/sets/${set.type}/${set.id}/questions`);
      setRows((res.data?.data || []).filter((q) => ["completed", "flagged"].includes(q.state)));
    } catch (e) {
      setRows([]);
      toast.error(e.response?.data?.message || "Could not load this set's videos.");
    }
  }, [set.type, set.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (rows === null) return <LinearProgress sx={{ my: 1 }} />;
  if (rows.length === 0) return <Typography variant="body2" color="text.secondary">No finished videos.</Typography>;

  return (
    <>
      <Table size="small">
        <TableBody>
          {rows.map((q) => (
            <TableRow key={q.id}>
              <TableCell width={48}>Q{q.number}</TableCell>
              <TableCell>
                <Typography variant="body2">{q.text}</Typography>
                {q.verification === "rejected" && q.verification_notes && (
                  <Typography variant="caption" color="error.main" display="block">
                    {q.verification_notes}
                  </Typography>
                )}
              </TableCell>
              <TableCell width={260}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {q.state === "flagged" && <StateChip state="flagged" />}
                  <VerificationChip question={q} />
                  {q.pending_video_url && <Chip size="small" color="info" variant="outlined" label="New version" />}
                  {q.published && <Chip size="small" color="success" variant="outlined" label="Published" />}
                </Stack>
              </TableCell>
              <TableCell width={200} align="right">
                <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                  <VideoExplanationPlayer allowDownload videoUrl={q.pending_video_url || q.video_url} questionText={q.text} />
                  {canVerify && (
                    <Button size="small" variant={q.verification ? "text" : "outlined"} color="warning" onClick={() => setVerifying(q)}>
                      {q.verification ? "Re-verify" : "Verify"}
                    </Button>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <VideoVerifyDialog
        open={Boolean(verifying)}
        type={set.type}
        question={verifying}
        onClose={() => setVerifying(null)}
        onSaved={() => {
          load();
          onChanged();
        }}
      />
    </>
  );
}

function SetRow({ set, canVerify, onChanged }) {
  const [open, setOpen] = useState(false);
  const done = set.counts.completed;
  const v = set.verification || {};
  const pct = done > 0 ? Math.round(((v.verified || 0) / done) * 100) : 0;

  return (
    <>
      <TableRow hover>
        <TableCell width={40}>
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label="Show videos">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {set.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[`#${set.id}`, set.subject, set.difficulty].filter(Boolean).join(" · ")}
          </Typography>
        </TableCell>
        <TableCell align="center" width={150}>
          <TypeChip type={set.type} />
        </TableCell>
        <TableCell width={220}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={pct >= 100 ? "success" : "primary"}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" sx={{ minWidth: 44 }}>
              {v.verified || 0}/{done}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {v.awaiting > 0 && <Chip size="small" color="warning" variant="outlined" label={`${v.awaiting} awaiting`} />}
            {v.rejected > 0 && <Chip size="small" color="error" variant="outlined" label={`${v.rejected} rejected`} />}
            {v.verified_unpublished > 0 && (
              <Chip size="small" color="info" variant="outlined" label={`${v.verified_unpublished} to publish`} />
            )}
            {done > 0 && <Chip size="small" variant="outlined" label={`Published ${set.published}/${done}`} />}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : "none" }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <SetVideos set={set} canVerify={canVerify} onChanged={onChanged} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/**
 * Verification Dashboard "Videos" tab: sets with finished explanation videos and how many a
 * tester has verified. Open a set to watch each video and approve or reject it.
 */
export default function VideoVerificationPanel() {
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("awaiting_verification");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const canVerify = canVerifyVideos();

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const res = await api.get("/admin/explanation-videos/sets", {
          params: { search: search.trim() || undefined, type: type || undefined, status, page: page + 1, per_page: rowsPerPage },
        });
        setData({ items: res.data?.data?.items || [], total: res.data?.data?.total || 0 });
      } catch (e) {
        setError(e.response?.data?.message || "Could not load the videos.");
      } finally {
        setLoading(false);
      }
    },
    [search, type, status, page, rowsPerPage]
  );

  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    setPage(0);
  }, [search, type, status]);

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: "center" }}>
        <TextField
          size="small"
          placeholder="Search sets by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, val) => val !== null && setType(val)}>
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="worksheet">Worksheets</ToggleButton>
          <ToggleButton value="mock">Mock Exams</ToggleButton>
          <ToggleButton value="paper">Practice Papers</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ flex: 1 }} />
        <Button component={RouterLink} to="/admin/explanation-videos" size="small" sx={{ textTransform: "none" }}>
          Open Explanation Videos
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {COVERAGE.map((c) => (
          <Chip
            key={c.value}
            size="small"
            clickable
            label={c.label}
            color={status === c.value ? "primary" : "default"}
            variant={status === c.value ? "filled" : "outlined"}
            onClick={() => setStatus(c.value)}
          />
        ))}
      </Stack>
      {!canVerify && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can see video verification here; approving or rejecting videos needs the "verify-explanation_video" permission.
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Set</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <Tooltip title="Finished videos a tester has approved">
                  <span>Verified</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((set) => (
              <SetRow key={`${set.type}-${set.id}`} set={set} canVerify={canVerify} onChanged={() => load({ quiet: true })} />
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                    Nothing matches these filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {loading && data.items.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}
